/**
 * **Le dossier MeTube** — les strikes, la spirale de bad buzz et la pause en cours, rendus
 * visibles.
 *
 * Le PLAN pose que **tout échec est télégraphié et évitable**. Deux fins de partie manquaient
 * à l'appel : le joueur pouvait encaisser des strikes sans jamais savoir qu'à trois la chaîne
 * saute, et voir sa réputation décrocher sans qu'on lui dise que le public a changé d'humeur.
 * Le moteur porte les deux depuis M1 (`engine/gameover.ts` §11.2 et §11.4) ; rien ne les
 * affichait.
 *
 * ## Ce que ce bandeau montre — et pourquoi seulement ça
 *
 * - **Les strikes actifs**, leur compte sur `STRIKES_FATAL`, et le **jour d'expiration** du
 *   plus proche. Un strike vit `H_STRIKE` heures simulées : il s'efface tout seul, et c'est
 *   une information de survie — savoir qu'il suffit d'attendre change complètement la façon
 *   de jouer les semaines qui suivent.
 * - **L'arc de la chute** (`flags.arc_chute_active`), avec ses **deux seuils** : celui qui
 *   l'a ouvert et celui qui le referme. Un joueur qui ne connaît pas le second croit que
 *   c'est définitif.
 * - **Le dernier coup dur** (`state.last_damage`), mais **uniquement pendant l'arc** : ce
 *   champ est réécrit par n'importe quel choix d'événement qui coûte des abonnés ou de la
 *   réputation, donc l'afficher en permanence en ferait un bandeau toujours allumé, c'est-à-dire
 *   un bandeau qu'on ne lit plus. Pendant l'arc, il est le **contexte** qui explique la chute.
 * - **⟦A-2 / A-103⟧ La pause annoncée en cours** (`state.pause_announced`) : le seul endroit
 *   où l'état de pause était lisible était l'écran `/souffler` — jamais dans la chambre, où le
 *   joueur passe le plus clair de son temps entre deux vidéos. « En pause annoncée — Jn »
 *   compte les jours écoulés DEPUIS le début de cette pause (pas le jour absolu de la run), et
 *   dit si elle protège (gel du silence, SM ×1,5) ou si elle est hors fenêtre des 90 jours —
 *   auquel cas elle ne protège plus de rien, exactement ce que `demarrerPauseAuSommeil`
 *   (`pause.ts`) applique déjà en silence.
 *
 * ## L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧)
 *
 * Zéro strike, pas d'arc, pas de pause en cours ⇒ **le composant ne rend rien**. Il n'apprend
 * pas la règle à un joueur qui ne la rencontre jamais, et il n'occupe pas un pixel de la
 * chambre pour rien.
 *
 * ## Aucune constante recopiée
 *
 * `STRIKES_FATAL`, `H_STRIKE`, `ARC_OUVERTURE_REP`, `ARC_FERMETURE_REP` viennent toutes de
 * `@maxime/shared`, et `strikesActifs()` est **la** fonction du moteur qui décide lesquels
 * comptent (elle filtre sur `expire > clock_h`). Le client ne refait aucun de ces tris — la
 * pause non plus : `pause.protege` est lu tel quel, jamais redérivé de `last_pause_bonifiee_h`.
 *
 * ## ⟦C57 · lot 3 · A-174⟧ Le compteur et UNE phrase, le reste derrière « Les dessous »
 *
 * Le bandeau des strikes pesait 55 mots dans la colonne de 280 px de « Ma chaîne » : on ne lisait
 * plus rien. À l'écran il ne reste que le titre chiffré (« 2 strikes sur 3 » et ses trois jetons) et
 * UNE phrase de 12 mots au plus ; la règle d'expiration (« s'efface tout seul au bout de 90 jours, le
 * plus ancien tombe au jour N »), le dernier coup encaissé et le texte de la pause s'ouvrent par le
 * pictogramme 📰. Les phrases d'avant sont reprises MOT POUR MOT dans les dessous : rien n'est réécrit.
 */

import {
  ARC_FERMETURE_REP,
  ARC_OUVERTURE_REP,
  H_STRIKE,
  STRIKES_FATAL,
  jour as jourMoteur,
  strikesActifs,
  type GameState,
} from "@maxime/shared";

import { nombre } from "../game/format";
import { interrupteurs } from "../game/menage";
import { DessousBloc, LesDessous } from "./LesDessous";

/** Jours simulés que dure un strike — lu de `H_STRIKE`, jamais des « 90 » écrits à la main. */
const JOURS_DE_STRIKE = jourMoteur(H_STRIKE);

export function AlertesChaine({ state }: { state: GameState }) {
  // ⟦C60 · A-177⟧ Les strikes et le bad buzz comme sanction sont coupés : leurs deux bandeaux n'existent plus
  // (même une run commencée avec un strike encore actif ne l'affiche pas : le moteur n'en fait plus rien).
  // Reste la pause annoncée, qui n'est pas une sanction mais un repos. Rallumés, les deux bandeaux reviennent.
  const sys = interrupteurs();
  const actifs = sys.strikes ? strikesActifs(state) : [];
  const arc = sys.bad_buzz && state.flags.arc_chute_active === true;
  const pause = state.pause_announced;
  if (actifs.length === 0 && !arc && !pause) return null;

  return (
    <div className="alertes-chaine" role="status">
      {actifs.length > 0 ? <BlocStrikes state={state} actifs={actifs} /> : null}
      {arc ? <BlocArcDeLaChute state={state} reputationVisible={sys.reputation} /> : null}
      {pause ? <BlocPauseAnnoncee state={state} pause={pause} /> : null}
    </div>
  );
}

/** ⟦A-2 point 4 / A-103⟧ Le bandeau « En pause annoncée — Jn » que M2 promettait sur le tableau
 * de bord — jusqu'ici l'état de pause n'était lisible que sur l'écran `/souffler`. */
function BlocPauseAnnoncee({
  state,
  pause,
}: {
  state: GameState;
  pause: NonNullable<GameState["pause_announced"]>;
}) {
  const joursEcoules = jourMoteur(state.clock_h) - jourMoteur(pause.since_h);

  return (
    <p className={`alerte alerte-pause${pause.protege ? "" : " critique"}`}>
      <span className="alerte-icone" aria-hidden="true">
        🫖
      </span>
      <span className="alerte-corps">
        <strong>En pause annoncée — J{nombre(joursEcoules)}</strong>
        <span className="alerte-texte">
          {pause.protege ? " Le silence ne te punit pas." : " Cette pause ne protège de rien."}
        </span>
      </span>
      <LesDessous titre="La pause annoncée" variante="icone" className="alerte-dessous">
        <DessousBloc>
          <p className="dessous-texte">
            {pause.protege
              ? "Le silence ne te punit pas : Momentum et Fidélité sont gelés, ta santé mentale remonte plus vite. Ta première vidéo de retour en profitera."
              : "Hors fenêtre des 90 jours : cette pause-là ne protège de rien, l'audience n'y croit plus."}
          </p>
        </DessousBloc>
      </LesDessous>
    </p>
  );
}

function BlocStrikes({ state, actifs }: { state: GameState; actifs: readonly number[] }) {
  const restants = STRIKES_FATAL - actifs.length;
  // Le plus proche : c'est celui qui va libérer une place, donc le seul qui intéresse.
  const prochaineExpiration = Math.min(...actifs);
  const jourExpiration = jourMoteur(prochaineExpiration);
  const joursAAttendre = Math.max(0, jourExpiration - jourMoteur(state.clock_h));

  return (
    <p className={`alerte alerte-strikes${restants <= 1 ? " critique" : ""}`}>
      <span className="alerte-icone" aria-hidden="true">
        ⛔
      </span>
      <span className="alerte-corps">
        <strong>
          {nombre(actifs.length)} strike{actifs.length > 1 ? "s" : ""} sur {STRIKES_FATAL}
        </strong>{" "}
        <span className="strike-jetons" aria-hidden="true">
          {Array.from({ length: STRIKES_FATAL }, (_, i) => (
            <i key={i} className={i < actifs.length ? "pris" : ""} />
          ))}
        </span>
        <span className="alerte-texte">
          {restants === 0
            ? "La chaîne est tombée."
            : restants === 1
              ? " Encore un et la chaîne saute."
              : ` Encore ${nombre(restants)} avant que la chaîne saute.`}
        </span>
      </span>
      {/* ⟦C57⟧ Les phrases d'avant, mot pour mot : la gravité, la règle d'expiration, le jour du plus ancien. */}
      <LesDessous titre="Les strikes" variante="icone" className="alerte-dessous">
        <DessousBloc>
          <p className="dessous-texte">
            {restants === 0
              ? "La chaîne est tombée."
              : restants === 1
                ? "Encore un et la chaîne saute — c'est une fin de partie, pas un avertissement."
                : `Il en reste ${nombre(restants)} avant que la chaîne ne saute.`}
            {" Un strike s'efface tout seul au bout de "}
            {nombre(JOURS_DE_STRIKE)} jours : le plus ancien tombe au <strong>jour {nombre(jourExpiration)}</strong>
            {joursAAttendre > 0 ? ` (dans ${nombre(joursAAttendre)} jours)` : ""}.
          </p>
        </DessousBloc>
      </LesDessous>
    </p>
  );
}

/**
 * ⚠️ **Ce bloc décrit un ÉTAT, il ne promet aucune conséquence** — et c'est délibéré.
 *
 * Vérifié dans le moteur avant d'écrire ce texte : l'arc de la chute est bien posé et retiré
 * par `majArcDeLaChute` (`engine/index.ts`), mais sa seule issue mortelle passe par
 * `resoudreExcuses`, **qui n'a aucun appelant de production** — la vidéo d'excuses n'est
 * branchée à aucun choix de `content/events.json`. Aucun événement du contenu ne lit non plus
 * `arc_chute_active` ni la réputation dans ses conditions.
 *
 * Écrire ici « chaque tuile tape plus fort » serait donc un mensonge d'interface, exactement
 * ce que le projet s'interdit (« mieux vaut un trou visible qu'une promesse creuse »). On dit
 * donc ce qui est vrai et actionnable : la valeur, les deux seuils, et le dernier coup encaissé.
 */
function BlocArcDeLaChute({ state, reputationVisible }: { state: GameState; reputationVisible: boolean }) {
  const degats = state.last_damage;
  // ⟦C60 bis · A-177⟧ **La jauge n'a plus de nom à l'écran** (arbitrage n° 2 de Matt, 03/10/2026 : « la retirer de
  // l'écran »). Quand elle est éteinte, le bandeau dit l'état sans la nommer ni la chiffrer, et le « dernier coup » ne
  // compte plus que les abonnés perdus. Allumée, c'est le bandeau d'avant, mot pour mot.
  const perteDeReputation = reputationVisible ? degats?.rep_lost ?? 0 : 0;
  const dernierCoup =
    degats !== null && (degats.subs_lost > 0 || perteDeReputation > 0) ? (
      <>
        {" Le dernier coup date du jour "}
        {nombre(jourMoteur(degats.at_h))}
        {" : "}
        {degats.subs_lost > 0 ? `−${nombre(degats.subs_lost)} abonnés` : ""}
        {degats.subs_lost > 0 && perteDeReputation > 0 ? " et " : ""}
        {perteDeReputation > 0 ? `−${nombre(perteDeReputation)} de réputation` : ""}.
      </>
    ) : null;
  return (
    <p className="alerte alerte-arc critique">
      <span className="alerte-icone" aria-hidden="true">
        📉
      </span>
      <span className="alerte-corps">
        <strong>Le public sent le sang.</strong>
        {/* ⟦C57⟧ Le chiffre qui compte (la réputation, le seuil à regagner) ; les deux seuils et le
            dernier coup rouvrent derrière le pictogramme. */}
        <span className="alerte-texte">
          {reputationVisible ? (
            <>
              {" Réputation "}
              {nombre(state.reputation)}
              {" : remonte au-dessus de "}
              <strong>{nombre(ARC_FERMETURE_REP)}</strong>.
            </>
          ) : (
            " Ça se calme avec le temps, pas en un jour."
          )}
        </span>
      </span>
      <LesDessous titre="Le public sent le sang" variante="icone" className="alerte-dessous">
        <DessousBloc>
          <p className="dessous-texte">
            {reputationVisible ? (
              <>
                {"Ta réputation est tombée à "}
                {nombre(state.reputation)}
                {", sous la barre des "}
                {nombre(ARC_OUVERTURE_REP)}
                {". Ramène-la au-dessus de "}
                <strong>{nombre(ARC_FERMETURE_REP)}</strong>
                {"."}
              </>
            ) : (
              "Le public t'est tombé dessus, et il ne lâche pas du jour au lendemain."
            )}
            {dernierCoup}
          </p>
        </DessousBloc>
      </LesDessous>
    </p>
  );
}
